import type { Quote, QuoteStatus } from '../api/types'
import { quoteStatusLabels } from '../ui/status-labels'
import { numberValue } from '../ui/numbers'

export type StatusFilter = 'all' | 'open' | Exclude<QuoteStatus, 'archived'>

export const statusFilters: ReadonlyArray<{ value: StatusFilter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'open', label: 'Open' },
  { value: 'started', label: 'Started' },
  { value: 'draft', label: 'Draft' },
  { value: 'sent', label: 'Sent' },
  { value: 'viewed', label: 'Viewed' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Lost' },
  { value: 'expired', label: 'Expired' },
]

export const quoteSell = (quote: Quote) => numberValue(quote.totals.sell)
export const quoteNet = (quote: Quote) => numberValue(quote.totals.net)
export const quoteProfit = (quote: Quote) => numberValue(quote.totals.profit)
export const quoteRebate = (quote: Quote) => numberValue(quote.totals.rebate)
/** Sell less any rebate: what the quote brings in. */
export const quoteRevenue = (quote: Quote) => quoteSell(quote) - quoteRebate(quote)

export function isOpenQuote(quote: Quote) {
  return ['started', 'draft', 'sent', 'viewed'].includes(quote.status)
}

export function matchesStatus(quote: Quote, filter: StatusFilter) {
  return filter === 'all' || (filter === 'open' ? isOpenQuote(quote) : quote.status === filter)
}

/** Quotes created this month and quotes approved this month (by approval date). */
export function quotesThisMonth(quotes: Quote[], now = new Date()) {
  const start = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1)
  const end = Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1)
  const inMonth = (value: string) => {
    const time = new Date(value).getTime()
    return time >= start && time < end
  }
  return {
    created: quotes.filter((quote) => inMonth(quote.created_at)).length,
    approved: quotes.filter((quote) => quote.approval_events?.some(inMonth)).length,
  }
}

/** Every search word must appear in the quote number, name, customer, or any line's product. */
export function matchesQuoteSearch(quote: Quote, search: string) {
  const terms = search.trim().toLocaleLowerCase('en-US').split(/\s+/).filter(Boolean)
  if (!terms.length) return true
  const text = [
    quote.quote_number,
    quote.title,
    quote.customer?.company_name,
    ...quote.lines.flatMap((line) => [
      line.model,
      line.manufacturer,
      line.description,
      line.equipment_description,
      ...Object.values(line.configuration_display ?? {}),
      ...(line.configuration_items ?? []).flatMap((item) => [item.model, item.description]),
    ]),
  ]
    .filter((value): value is string => typeof value === 'string' && Boolean(value))
    .join(' ')
    .toLocaleLowerCase('en-US')
  return terms.every((term) => text.includes(term))
}

/** Download the listed quotes as a CSV, with the columns Sales reports on. */
export function exportQuotes(quotes: Quote[]) {
  const escape = (value: string | number) => `"${String(value).replaceAll('"', '""')}"`
  const rows = [
    ['Quote #', 'Quote Name', 'Customer', 'Due by', 'Latest Deliver By', 'Sell Total', 'Net', 'Rebate', 'Profit', 'Margin %', 'Status', 'Activity'],
    ...quotes.map((quote) => [
      quote.quote_number,
      quote.title,
      quote.customer?.company_name ?? '',
      quote.quote_due_date ?? '',
      quote.latest_delivery_date ?? '',
      quoteSell(quote).toFixed(2),
      quoteNet(quote).toFixed(2),
      quoteRebate(quote).toFixed(2),
      quoteProfit(quote).toFixed(2),
      numberValue(quote.totals.margin_percent).toFixed(2),
      quoteStatusLabels[quote.status],
      quote.latest_activity?.created_at ?? quote.updated_at,
    ]),
  ]
  const blob = new Blob([rows.map((row) => row.map(escape).join(',')).join('\n')], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = 'quotes.csv'
  link.click()
  URL.revokeObjectURL(url)
}
